// ============================================================================
// @insio/validation - Core Validation Types
// ============================================================================

import { SupportedLocale, UserContext } from '@insio/shared-types';
import type { IssueCode } from './issue-codes';

export const Severity = {
  error: 'error',
  warning: 'warning',
  /** Shown to the user, never counted: it leaves a SUCCESS validation status as it is */
  info: 'info'
} as const;

/** Severity levels ordered by importance */
export const SEVERITY_ORDER = [Severity.error, Severity.warning, Severity.info] as const;
export type Severity = (typeof Severity)[keyof typeof Severity];

/**
 * A single validation issue found during validation.
 * Generic `Meta` allows typed metadata per-rule.
 */
export interface BaseIssue<Meta = Record<string, unknown>> {
  /** How severe this issue is */
  severity: Severity;
  /** Dot-path to the field (e.g. 'items.0.amount') */
  path: string;
  /** Machine-readable error code (e.g. 'supplierMissing') */
  code: IssueCode;
  /** Optional structured metadata for UI/logic (e.g. { existingId, threshold }) */
  meta?: Meta;
}

export interface Issue<Meta = Record<string, unknown>> extends BaseIssue<Meta> {
  /** Human-readable message (localized) */
  message: string;

  /** Which group(s) this issue belongs to */
  groups: string[];

  /** Which rule produced this issue */
  ruleId?: string;

  /** Optional i18n key for this issue (defaults to code) */
  localeKey?: string;
}

/**
 * Context passed to every rule. Extend with your own fields.
 * Typically contains tenantId, userId, locale, etc.
 */
export interface ValidationContext {
  /** Locale for i18n message translation (e.g., 'en', 'cs'). */
  readonly locale: SupportedLocale;

  readonly currentUser?: UserContext | null;
}

/**
 * Serialized validation result for API transport.
 */
export interface SerializedResult {
  hasErrors: boolean;
  issues: Issue[];
  counts: Record<Severity, number>;
}

/**
 * A validation rule is a function that inspects data and returns issues.
 *
 * - Can be sync or async
 * - Receives the data being validated and a context object
 * - Returns an array of Issues (empty = no problems)
 */
export type RuleFn<T, Ctx extends ValidationContext = ValidationContext> = (
  data: T,
  ctx: Ctx
) => BaseIssue[] | Promise<BaseIssue[]>;

export type WhenFn<T, Ctx extends ValidationContext = ValidationContext> = (
  data: T,
  ctx: Ctx
) => boolean | Promise<boolean>;

/**
 * Full rule descriptor with metadata for the engine.
 */
export interface Rule<T, Ctx extends ValidationContext = ValidationContext> {
  /** Unique identifier for this rule */
  id: string;
  /** Human-readable description (for docs) */
  description: string;
  /** Group name(s) this rule belongs to (e.g. ['financial', 'compliance']) */
  groups?: string[];
  /** The validation function */
  validate: RuleFn<T, Ctx>;
  /** Higher priority rules run first. Default: 0 */
  priority?: number;
  /**
   * Which fields this rule covers (for dependency tracking / selective runs).
   * Dot notation for nested fields, array indices as needed (e.g. 'items.0.amount').
   * e.g. ['amount', 'currency'] — purely declarative metadata.
   */
  fields?: string[];
  /** If true, this rule is skipped. Useful for feature flags. */
  disabled?: boolean;
  /** Optional predicate — if it returns false, the rule is skipped. */
  when?: WhenFn<T, Ctx>;
  /** Optional i18n key prefix for the message (defaults to code or id) */
  localeKeyPrefix?: string;
}

/**
 * The result of running all validation rules.
 */
export interface ValidationResult<T> {
  /** The original data that was validated */
  data?: T;
  /** All issues found, in rule execution order */
  issues: Issue[];
  /** True if there are no errors (warnings are OK) */
  hasErrors: boolean;
  /** Convenience: just the errors */
  errors: Issue[];
  /** Convenience: just the warnings */
  warnings: Issue[];

  // ---- Field-level helpers ----

  /** Get all issues for a specific field path */
  byField(field: string): Issue[];
  /** Get the worst severity for a field (null if no issues) */
  worstFor(field: string): Severity | null;
  /** Check if a specific field has errors */
  hasErrorsOn(field: string): boolean;
  /** Check if a specific field has warnings */
  hasWarningsOn(field: string): boolean;

  // ---- Group helpers ----

  /** Get all issues for a specific rule group */
  byGroup(group: string): Issue[];
  /** Check if a group is valid (no errors) */
  isGroupValid(group: string): boolean;

  // ---- Serialization ----

  /** Serialize to a plain object (for API responses, GraphQL, etc.) */
  toJSON(): SerializedResult;
}
